import { FC, useCallback, useState } from 'react'
import { usePopper } from 'react-popper'
import { twMerge } from 'tailwind-merge'
import { useClickOutside } from 'shared/hooks/use-click-outside'
import { GIFObject } from 'modules/attachments/api/tenor.types'
import { GifPickerButton } from './gif-picker-button'
import { GifPickerPanel } from './gif-picker-panel'

interface GifPickerProps {
  className?: string
  onSelect: (gif: GIFObject) => void
  iconClassName?: string
}

export const GifPicker: FC<GifPickerProps> = ({ onSelect, className, iconClassName }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'top',
    strategy: 'fixed',
    modifiers: [
      {
        name: 'flip',
        enabled: true,
        options: {
          fallbackPlacements: ['top', 'bottom'],
        },
      },
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
    ],
  })

  const handleClickOutside = useCallback(() => setIsOpen(false), [setIsOpen])
  const ref = useClickOutside(handleClickOutside)

  const togglePicker = () => {
    setIsOpen(prev => !prev)
  }

  const closePicker = () => {
    setIsOpen(false)
  }

  return (
    <div ref={ref} className={twMerge('flex', className)}>
      <GifPickerButton
        iconClassName={iconClassName}
        setReferenceElement={setReferenceElement}
        isOpen={isOpen}
        onToggle={togglePicker}
      />
      {isOpen && (
        <div
          className="z-30"
          ref={setPopperElement}
          style={{ ...styles.popper }}
          {...attributes.popper}
        >
          <GifPickerPanel onSelect={onSelect} onClose={closePicker} />
        </div>
      )}
    </div>
  )
}
